import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useIntl } from 'umi';
import CardCommon from '@sg/components/CardCommon';
import { Justify, notification, Segment } from 'tea-component';
import RenderOperations from '@sg/components/Chart/Operations';
import Chart from '@sg/components/Chart';
import { pickOperations } from '@sg/commonData/pickOperations';
import TableContent from '@sg/components/TableContent';
import { scrollable } from 'tea-component/lib/table/addons';
import * as echarts from 'echarts';
import { chartGradientColors, chartGradientNOColors, dealTypeValue } from '@sg/utils/useChartOptions';
import TimeLine from '@sg/components/TimeLine';
import PipelineCalendar from '@sg/pages/StudioManagement/PortfolioOverview/components/PipelineCalendar';
import { getStudioOverview, getStudioOverviewTrend } from '@sg/services/index';
import DataProcessing from '@sg/components/DataProcessing';
import { COEFFICIENT, STUDIO_OVERVIEW_TOTAL_REVENUE_CARD, STUDIO_OVERVIEW_TOTAL_PROFIT_CARD } from '../config';
import './index.scss';
import { mathFormat } from '@sg/utils/common';
import { buildPeriod, buildUpdateTime } from '@sg/utils/common';
import MetricCard from '@/pages/strategic/pages/BusinessManagement/MetricCard';
import { xAxisHighLight } from '@sg/utils/chatUtils';
import { DefaultChat, RevenueTrendColums } from './pageConfig';
import { height, rendTitle } from '@sg/utils/commonPageMethod';
import PhaseStudioTable from './components/PhaseStudioTable';
// @ts-ignore
import { getButtonPermission } from '@sg/_config/permission';
import { usePermissionReady } from '@sg/hooks/usePermissionReady';
import { SGBuriedBtnPoint, createBtnPoint, PortfolioOverviewTab } from '@/pages/strategic/utils/buriedPoint';
// import EventWindows from '@op/pages/Overview/KeyMetrics/MentionsSentiment/MetricsChart/Charts/BarAndLine/EventWindows';
import EventWindows from '@sg/components/EventWindows';
import EventSetting from '@sg/components/EventSetting';
import { buildTrendTooltipFormatter } from '../buildTrendTooltipFormatter';
import PlainText from "@sg/components/PlainText";
import Loading from '@intelligence/components/Loading';
import { useNotesAutoScroll } from './hooks/useNotesAutoScroll';
import usePipelineCalendarAutoScroll from './hooks/usePipelineCalendarAutoScroll';
import { usePipelineCalendarData } from './hooks/usePipelineCalendarData';
import { EDIT_PAGE_KEY_MAP } from '@/pages/strategic/hooks/useAutoSaveParams';
import moment from 'moment';
import EventToggleSetting from '@/pages/strategic/components/EventToggleSetting';
import useEventSetting from '@/pages/strategic/components/EventToggleSetting/useEventSetting';
import CommonBubble from '@home/components/CommonBubble';
import SvgIcon from '@/pages/home/components/SvgIcon';
import BurnRateCard from '../component/BurnRateCard';
import HeadcountCard from '../component/HeadcountCard';
import MetricsDownloadModal, {
  PORTFOLIO_METRICS_DOWNLOAD_CONFIG,
} from '../component/MetricsDownloadModal';
import { getOptionsSegment } from '../Studios/config';
import { useStudioEyesViewState, PORTFOLIO_EYES_VIEW_KEYS } from '../hooks/useStudioEyesViewState';

const PortfolioOverview: React.FC<any> = (props) => {
  const { selectedMonth, maxDataTime, onEditingChange } = props; // selectedMonth is already formatted as "YYYY-MM" string or null, maxDataTime is "YYYY-MM" or null
  const { formatMessage } = useIntl();
  const { ready: permissionReady } = usePermissionReady();
  const chartRef = useRef<any>(null);

  // Timeline data hook
  const { qAllData } = usePipelineCalendarData(selectedMonth);

  // Revenue Profit 状态
  const [activeCard, setActiveCard] = useState<any>('Revenue');
  const showRevenueTrendList = ['line', 'bar', 'table'];
  const [revenueData, setRevenueData] = useState<any>([]);
  const [allData, setAllData] = useState<any>({});
  const [showChatsType, setShowChatsType] = useState<any>(DefaultChat);

  const {
    chartEventSetting,
    setChartEventSetting,
    eventSettingVisible,
    setEventSettingVisible,
    isChartReady,
    setIsChartReady,
    eventDataByGame,
    showEventsDisabled,
  } = useEventSetting({
    revenueData,
    chartRef,
    showChatsTypeValue: showChatsType?.value,
    activeCard,
    eventType: 'overview',
  });

  // Wrapper function for setShowChatsType with buried point tracking
  const handleShowChatsTypeChange = (e: any, downloadValue?: string) => {
    // Determine trend type based on active card (Revenue or Profit)
    const trendType = activeCard === 'Profit' ? 'Profit' : 'Revenue';

    // Track buried point based on the selected value
    // For download, the second parameter is 'download' (only track when downloadValue is provided)
    // For view/line/bar/table, check e.value (but not when it's a download call)
    if (downloadValue === 'download') {
      // Only track download when the second parameter is explicitly 'download'
      // This prevents double tracking since RenderOperations calls change() twice for download
      const btnPoint = createBtnPoint(
        PortfolioOverviewTab,
        'revenueTrendDownload',
        `${trendType} Trend - Download`
      );
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }
    } else if (e?.value && e.value !== 'download') {
      // Track view/line/bar/table only when e.value is set and it's not 'download'
      const value = e.value;

      // Check if this is a view button click
      // View button has isNoSelect: true, so it toggles format but doesn't change operation
      // When view is clicked, change() is called with current operation's data, not view's data
      // We detect view clicks by checking if format changed but value stayed the same
      const isViewClick = e.format !== showChatsType?.format && value === showChatsType?.value;

      if (value === 'view' || isViewClick) {
        // Track view button click
        // For view button, we track every time format changes (toggle)
        const btnPoint = createBtnPoint(
          PortfolioOverviewTab,
          'revenueTrendView',
          `${trendType} Trend - View`
        );
        if (btnPoint) {
          SGBuriedBtnPoint(btnPoint);
        }
      } else if (value === 'line') {
        // Only track if switching to line (not already on line)
        if (showChatsType?.value !== 'line') {
          const btnPoint = createBtnPoint(
            PortfolioOverviewTab,
            'revenueTrendLineChart',
            `${trendType} Trend - Line Chart`
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }
      } else if (value === 'bar') {
        // Only track if switching to bar (not already on bar)
        if (showChatsType?.value !== 'bar') {
          const btnPoint = createBtnPoint(
            PortfolioOverviewTab,
            'revenueTrendBarChart',
            `${trendType} Trend - Bar Chart`
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }
      } else if (value === 'table') {
        // Only track if switching to table (not already on table)
        if (showChatsType?.value !== 'table') {
          const btnPoint = createBtnPoint(
            PortfolioOverviewTab,
            'revenueTrendTable',
            `${trendType} Trend - Table`
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }
      }
    }

    // Call the original setter
    setShowChatsType(e);
  };

  // Studio Overview API data state
  const [studioOverviewData, setStudioOverviewData] = useState<any>(null);
  const [studioOverviewLoading, setStudioOverviewLoading] = useState<boolean>(false);
  const [studioOverviewDataTime, setStudioOverviewDataTime] = useState<string | null>(null);

  // Cache the raw trend data from API (called only once on mount)
  const [rawTrendData, setRawTrendData] = useState<any[]>([]);
  const [trendLoading, setTrendLoading] = useState<boolean>(false);
  const trendRequestIdRef = useRef(0);

  const [isPhaseEditing, setIsPhaseEditing] = useState(false);
  const [isNotesEditing, setIsNotesEditing] = useState(false);

  // Persist eye/view via cond API (apiKeys defined on PORTFOLIO_EYES_VIEW_KEYS).
  const [metricsDateTypeSegment, setMetricsDateTypeSegment] = useState('month');
  const [metricsDownloadVisible, setMetricsDownloadVisible] = useState(false);
  const { smallChartViewState, updateStudioEyesViewState } = useStudioEyesViewState({
    viewKeys: PORTFOLIO_EYES_VIEW_KEYS,
  });
  const metricsSegmentOptions = useMemo(
    () => getOptionsSegment(formatMessage),
    [formatMessage],
  );

  const buttonPermission = useMemo(() => ({
    grossRevenueTrend: getButtonPermission('studio_overview_grossRevenueTrend'),
    notes: getButtonPermission('studio_overview_notes'),
    pipelineCalendar: getButtonPermission('studio_overview_pipelineCalendar'),
  }), [permissionReady]);

  const notesRef = useRef<HTMLDivElement>(null);
  const pipelineCalendarRef = useRef<HTMLDivElement>(null);
  // Auto-scroll to Notes when returning from document preview
  useNotesAutoScroll(notesRef);
  // Auto-scroll to Pipeline Calendar when returning from Live Titles / In Dev Projects
  usePipelineCalendarAutoScroll(pipelineCalendarRef);

  // Convert selectedMonth string to moment object for PlainText component
  // Use studioOverviewDataTime from API if available, otherwise fall back to selectedMonth
  const selectedMonthMoment = useMemo(() => {
    const dateStr = studioOverviewDataTime || selectedMonth;
    return dateStr ? moment(dateStr, 'YYYY-MM') : null;
  }, [studioOverviewDataTime, selectedMonth]);

  // Compute params for PlainText Notes component
  // If studioOverviewDataTime exists, don't include logical_flag (use date from selectedMonthMoment)
  // If studioOverviewDataTime doesn't exist, include logical_flag to get latest data
  const notesParams = useMemo(() => {
    const baseParams: any = {
      source: "portfolio",
    };

    // As requirement stated only "PortfolioOverview" and "Studios" have to follow the updateTime provided by studioOverviewDataTime
    // So logical flag is optional here only if we don't have studioOverviewDataTime
    if (!studioOverviewDataTime) {
      baseParams.logical_flag = "has_date_latest";
    }

    return baseParams;
  }, [studioOverviewDataTime]);

  const handlePhaseEditingChange = (isEditing: boolean) => {
    setIsPhaseEditing(isEditing);
    // Notify parent component about editing state change to disable the date selection
    onEditingChange?.(isEditing || isNotesEditing);
  };

  const handleNotesEditingChange = (isEditing: boolean) => {
    setIsNotesEditing(isEditing);
    // Notify parent component about editing state change to disable the date selection
    onEditingChange?.(isPhaseEditing || isEditing);
  };

  const [currentMetricsData, setCurrentMetricsData]: any = useState({
    actual: {
      title: 'Monthly Actual',
      date: '',
      data: null,
      content: 'MoM',
      rate: ''
    },
    // forecast: {
    //   title: 'Monthly Forecast',
    //   date: '',
    //   data: null,
    //   content: 'MoM',
    //   rate: ''
    // },
    kpi: {
      title: 'Monthly KPI',
      date: '',
      data: null,
      content: 'KPI Completion',
      rate: ''
    },
  });

  const resetCurrentMetricsData = () => {
    setCurrentMetricsData({
      actual: {
        title: 'Monthly Actual',
        date: '',
        data: null,
        content: 'MoM',
        rate: ''
      },
      kpi: {
        title: 'Monthly KPI',
        date: '',
        data: null,
        content: 'KPI Completion',
        rate: ''
      },
    });
  };

  useEffect(() => {
    setShowTrendList(revenueData);
  }, [revenueData, showChatsType, formatMessage, activeCard, chartEventSetting.showEventBubbles]); // Added formatMessage for toggling language in charts

  // Fetch trend data from API based on activeCard type (Revenue/Profit)
  const fetchStudioOverviewTrend = async () => {
    const requestId = ++trendRequestIdRef.current;
    try {
      setTrendLoading(true);
      // Clear current chart/table state immediately to avoid flashing old card data.
      setRawTrendData([]);
      setRevenueData([]);
      setAllData({});
      setIsChartReady(false);
      resetCurrentMetricsData();

      const type: 'revenue' | 'profit' = activeCard === 'Profit' ? 'profit' : 'revenue';
      // Build payload with type and optional datatime parameter (already formatted as "YYYY-MM")
      const payload: { type: 'revenue' | 'profit'; datatime?: string } = { type };
      if (selectedMonth) {
        payload.datatime = selectedMonth;
      }

      const res: any = await getStudioOverviewTrend(payload);

      const trendData = res?.data?.data || [];
      if (requestId !== trendRequestIdRef.current) {
        return;
      }
      setRawTrendData(trendData);
    } catch (error) {
      if (requestId !== trendRequestIdRef.current) {
        return;
      }
      console.error('Error fetching studio overview trend:', error);
      notification.error({
        description: 'Failed to load trend data'
      });
      setRawTrendData([]);
    } finally {
      if (requestId === trendRequestIdRef.current) {
        setTrendLoading(false);
      }
    }
  };

  // Transform raw trend data into chart-ready format
  // API now returns flat monthly data for the requested type (revenue or profit)
  useEffect(() => {
    if (!rawTrendData || rawTrendData.length === 0) {
      setRevenueData([]);
      resetCurrentMetricsData();
      return;
    }

    // Data is already flat per type — map directly
    const transformedData = rawTrendData.map((monthData: any) => ({
      month: monthData.month,
      actual: monthData.actual,
      forecast: monthData.forecast,
      kpi: monthData.kpi,
      mom_actual: monthData.mom_actual,
      mom_forecast: monthData.mom_forecast,
      kpi_completion: monthData.kpi_completion,
      projects: monthData.projects || [],
    }));

    // console.log(`Transformed ${activeCard} data:`, transformedData);
    setRevenueData(transformedData);

    // Get the last month's data before actual hits null
    const lastValidData = transformedData.findLast((item: any) => item.actual !== null);

    if (lastValidData) {
      setCurrentMetricsData({
        actual: {
          title: 'Monthly Actual',
          date: lastValidData.month,
          data: lastValidData.actual,
          content: 'MoM',
          rate: lastValidData.mom_actual || ''
        },
        // forecast: {
        //   title: 'Monthly Forecast',
        //   date: lastValidData.month,
        //   data: lastValidData.forecast,
        //   content: 'MoM',
        //   rate: lastValidData.mom_forecast || ''
        // },
        kpi: {
          title: 'Monthly KPI',
          date: lastValidData.month,
          data: lastValidData.kpi,
          content: 'KPI Completion',
          rate: lastValidData.kpi_completion || ''
        },
      });
    } else {
      resetCurrentMetricsData();
    }
  }, [rawTrendData, activeCard]); // Re-transform when rawTrendData or activeCard changes

  // Fetch studio overview data (Total Revenue and Total Profit capsules)
  const fetchStudioOverviewData = async () => {
    try {
      setStudioOverviewLoading(true);

      // Reset datatime state before fetching to ensure clean state
      setStudioOverviewDataTime(null);

      // Build payload with optional datatime parameter (already formatted as "YYYY-MM")
      const payload: { datatime?: string } = {};
      if (selectedMonth) {
        payload.datatime = selectedMonth;
      }

      const response = await getStudioOverview(payload);
      // console.log('Studio Overview API Response:', response);
      setStudioOverviewData(response?.data);

      // Extract datatime from response
      const datatime = response?.data?.profit?.datatime;
      if (datatime) {
        setStudioOverviewDataTime(datatime);
      }
      // If no datatime, studioOverviewDataTime remains null (already reset above)
    } catch (error) {
      console.error('Error fetching studio overview data:', error);
      notification.error({
        description: 'Failed to load overview data'
      });
      setStudioOverviewData(null);
      setStudioOverviewDataTime(null);
    } finally {
      setStudioOverviewLoading(false);
    }
  };

  // 初始化 - only run once on mount
  useEffect(() => {
    fetchStudioOverviewData();
  }, []); // Empty deps - only run on mount

  // Fetch trend data when activeCard changes (API requires type: revenue/profit)
  useEffect(() => {
    fetchStudioOverviewTrend();
  }, [activeCard]);

  const setShowTrendList = (data: any) => {
    const chartData: any = {};
    if (showRevenueTrendList.length === 0) return;
    showRevenueTrendList.map((item) => {
      if (item === 'table') {
        chartData[item] = {
          data,
          columns: RevenueTrendColums,
        };
        return '';
      }
      const xAxisData: any = [];
      const datas: any = {};
      // Store per-index projects for tooltip: monthly for bar, cumulative for line
      const projectsPerIndex: any[] = [];

      // Calculate cumulative values for line chart, use monthly values for bar chart
      if (showChatsType?.value === 'line') {
        // For line chart: calculate cumulative (YTD) values
        let cumulativeActual: number | null = null;
        let cumulativeForecast: number | null = null;
        let cumulativeKpi: number | null = null;
        let lastActualValue: number | null = null;
        let lastActualIndex: number | null = null;
        // Track cumulative project values: { [project_id]: { ...proj, actual: cumSum, kpi: cumSum } }
        const cumulativeProjectsMap: Record<string, any> = {};

        datas.Actual = [];
        datas.Forecast = [];
        datas.KPI = [];

        data.forEach((element: any, idx: number) => {
          xAxisData.push(element.month);

          // Accumulate project values across months
          const monthProjects = element.projects || [];
          monthProjects.forEach((proj: any) => {
            const pid = proj.project_id;
            if (!pid) return;
            const projValue = proj.actual ?? proj.forecast ?? null;
            const projKpi = proj.kpi ?? null;
            if (cumulativeProjectsMap[pid]) {
              if (projValue !== null) {
                cumulativeProjectsMap[pid].actual = (cumulativeProjectsMap[pid].actual ?? 0) + projValue;
              }
              if (projKpi !== null) {
                cumulativeProjectsMap[pid].kpi = (cumulativeProjectsMap[pid].kpi ?? 0) + projKpi;
              }
            } else {
              cumulativeProjectsMap[pid] = {
                ...proj,
                actual: projValue,
                kpi: projKpi,
              };
            }
          });

          // Snapshot current cumulative projects, sorted by descending actual value
          const cumulativeProjectsSnapshot = Object.values(cumulativeProjectsMap)
            .map((p: any) => ({ ...p }))
            .sort((a: any, b: any) => (b.actual ?? 0) - (a.actual ?? 0));
          projectsPerIndex.push(cumulativeProjectsSnapshot);

          // 处理 Actual 和 Forecast 的互斥关系
          // 如果 actual 存在，则 Actual 有值，Forecast 在该位置为 null
          if (element.actual !== null && element.actual !== undefined) {
            if (cumulativeActual === null) {
              cumulativeActual = element.actual;
            } else {
              cumulativeActual += element.actual;
            }
            datas.Actual.push(cumulativeActual);
            datas.Forecast.push(null);  // Forecast 在该位置为 null
            lastActualValue = cumulativeActual;
            lastActualIndex = idx;
          }
          // 如果只有 forecast 存在，则 Forecast 有值，Actual 在该位置为 null
          else if (element.forecast !== null && element.forecast !== undefined) {
            if (cumulativeActual === null) {
              cumulativeActual = lastActualValue ?? 0;
            }
            cumulativeActual += element.forecast;
            datas.Actual.push(null);  // Actual 在该位置为 null
            datas.Forecast.push(cumulativeActual);
          }
          // 如果都没有数据
          else {
            cumulativeActual = null;
            cumulativeForecast = null;
            datas.Actual.push(null);
            datas.Forecast.push(null);
          }

          // 处理 KPI
          if (element.kpi === null) {
            cumulativeKpi = null;
          } else if (cumulativeKpi === null) {
            cumulativeKpi = element.kpi;
          } else {
            cumulativeKpi += element.kpi;
          }

          datas.KPI.push(cumulativeKpi);
        });

        // 确保 Actual 和 Forecast 在最后一个 Actual 位置连接
        if (lastActualIndex !== null && lastActualIndex >= 0 && lastActualValue !== null) {
          const forecastValue = datas.Forecast[lastActualIndex];
          if (forecastValue === null || forecastValue === undefined) {
            datas.Forecast[lastActualIndex] = lastActualValue;
          }
        }
      } else if (showChatsType?.value === 'bar') {
        // For bar chart: use monthly values
        datas.Actual = [];
        datas.Forecast = [];
        datas.KPI = [];

        data.forEach((element: any) => {
          xAxisData.push(element.month);
          // Handle null values - push null as null to preserve the distinction
          datas.Actual.push(element.actual);
          datas.Forecast.push(element.forecast);
          datas.KPI.push(element.kpi);
          projectsPerIndex.push(element.projects || []);
        });
      }

      xAxisData.push('');

      // Build series data
      const seriesDataArray = Object.keys(datas).map((keys, index) => {
        const tdata: any = {
          name: formatMessage({ id: `dbs_charts_legend_${keys.toLowerCase()}` }),
          barGap: showChatsType?.value === 'bar' ? '-100%' : '0%',
          barWidth: '25%',
          data: datas[keys].map((items: any, i: number) => {
            if (showChatsType?.value === 'bar' && keys !== 'KPI') {
              if (datas?.Actual[i]) {
                return {
                  value: items,
                  names: activeCard,
                  projects: projectsPerIndex[i] || [],
                  itemStyle: {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                      { offset: 0, color: chartGradientColors[index]?.[1] },
                      { offset: 1, color: chartGradientColors[index]?.[0] }
                    ]),
                  },
                  label: {
                    position: datas?.Actual[i] > 0 ? 'top' : 'bottom',
                  },

                };
              }
              return {
                value: items,
                names: activeCard,
                projects: projectsPerIndex[i] || [],
                itemStyle: {
                  color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    { offset: 0, color: chartGradientNOColors[index]?.[1] },
                    { offset: 1, color: chartGradientNOColors[index]?.[0] }
                  ]),
                }
              };
            }
            const titleNameobj: any = {
              Profit: 'Profit',
              Revenue: 'Gross Revenue',
            };
            if (keys === 'Actual' && showChatsType?.value === 'line') {
              return {
                ...items,
                label: {
                  position: datas?.Actual[i] >= 0 ? 'top' : 'bottom',
                },
                value: items,
                names: `YTM ${titleNameobj[activeCard]}`,
                projects: projectsPerIndex[i] || [],
              };
            }
            return items;
          }),
          type: keys === 'KPI' && showChatsType?.value === 'bar' ? 'line' : showChatsType?.value,
          z: showChatsType?.value === 'bar' ? keys === 'KPI' ? 99 : Object.keys(datas).length - index : keys === 'Actual' ? 99 : Object.keys(datas).length - index,
          showSymbol: showChatsType?.format,
        };
        if (showChatsType?.value === 'line' || showChatsType?.value === 'bar') {
          tdata.lineStyle = {
            type: showChatsType?.value === 'bar' || keys === 'Actual' ? '' : 'dashed', // 线条类型，设置为'dashed'为虚线
            width: 2,
          };
          tdata.label = {
            show: keys === 'Actual' ? showChatsType?.format : false,
            formatter: (params: any) => mathFormat(params?.value),
            // position: datas?.Actual[i] > 0 ? 'top' : 'bottom',
            rich: {
              color: 'red',
              d: {
                color: '#fff',
                fontSize: 16,
                lineHeight: 24,
                height: 24
              },
            },
            textStyle: {
              color: '#262832',
              fontSize: 12,
            },
          };

        }
        return tdata;
      });

      // Add TopTipsEvent series for event positioning (hidden series used by EventWindows)
      // This must be added to seriesData to ensure it exists when chart initializes and convertToPixel is called
      // Add it unconditionally if showEventBubbles is enabled (like opinion module does)
      if (chartEventSetting.showEventBubbles) {
        const xAxisDataLength = xAxisData.length;
        const isBarChart = showChatsType?.value === 'bar';
        seriesDataArray.push({
          name: 'TopTipsEvent',
          type: isBarChart ? 'bar' : 'line',
          ...(isBarChart ? {
            barWidth: '25%',
            barGap: '-100%', // Same as other bars - 100% overlap
          } : {}),
          data: new Array(xAxisDataLength).fill(0),
          itemStyle: { color: 'transparent', opacity: 0 },
          lineStyle: { opacity: 0 },
          label: { show: false },
          tooltip: { show: false },
          silent: true,
          z: -1, // Behind other series
          hiddenFromLegend: true, // Hide from legend (custom property handled by DefaultChatsValue)
        });
      }

      chartData[item] = {
        xAxisData,
        type: showChatsType?.value,
        seriesData: seriesDataArray
      };
    });
    setAllData(chartData);
  };

  // Create configs with API data
  const revenueCardConfig = useMemo(() => STUDIO_OVERVIEW_TOTAL_REVENUE_CARD(studioOverviewData), [studioOverviewData]);

  const profitCardConfig = useMemo(() => STUDIO_OVERVIEW_TOTAL_PROFIT_CARD(studioOverviewData), [studioOverviewData]);

  return (
    <>
      <div className='dbs-portfolio-page'>
        <div className='dbs-home-studio-card'>
          <MetricCard
            key={revenueCardConfig?.title}
            coefficient={COEFFICIENT}
            config={revenueCardConfig}
            capsuleMode={'single'}
            capsuleHeight={40}
            cardKey={0}
            isActive={activeCard === 'Revenue' ? 1 : 2}
            onClick={() => {
              if (activeCard !== 'Revenue') {
                // Track buried point for Revenue metric card click
                const btnPoint = createBtnPoint(
                  PortfolioOverviewTab,
                  'revenueMetricCard',
                  'Revenue Metric Card'
                );
                if (btnPoint) {
                  SGBuriedBtnPoint(btnPoint);
                }
                setActiveCard('Revenue');
              }
            }}
          />
          <MetricCard
            key={profitCardConfig?.title}
            coefficient={COEFFICIENT}
            config={profitCardConfig}
            capsuleMode={'single'}
            capsuleHeight={40}
            cardKey={3}
            isActive={activeCard === 'Profit' ? 1 : 2}
            onClick={() => {
              if (activeCard !== 'Profit') {
                // Track buried point for Profit metric card click
                const btnPoint = createBtnPoint(
                  PortfolioOverviewTab,
                  'profitMetricCard',
                  'Profit Metric Card'
                );
                if (btnPoint) {
                  SGBuriedBtnPoint(btnPoint);
                }
                setActiveCard('Profit');
              }
            }}
          />
        </div>
        {/* Revenue/Profit Trend Chart */}
        <CardCommon
          title={rendTitle({
            title: activeCard === 'Profit' ? `${formatMessage({ id: "dbs_profit_trend" })} ($)` : `${formatMessage({ id: "dbs_gross_revenue_trend" })} ($)`,
            tips:
              //  Event Setting - only show for bar and line charts
              (['bar', 'line'].includes(showChatsType?.value)) && (
                <EventToggleSetting
                  showEventBubbles={chartEventSetting.showEventBubbles}
                  disabled={showEventsDisabled}
                  onToggle={(value) => {
                    const btnPoint = createBtnPoint(
                      PortfolioOverviewTab,
                      'showEvents',
                      `Show Events - ${value ? 'On' : 'Off'}`,
                    );
                    if (btnPoint) {
                      SGBuriedBtnPoint(btnPoint);
                    }
                    setChartEventSetting({
                      ...chartEventSetting,
                      showEventBubbles: value,
                    });
                  }}
                  onOpenSettings={() => {
                    setEventSettingVisible(true);
                  }}
                />
              ),
          })}
          className='card-common-Inertia'
          operation={
            <RenderOperations
              defaultValue={showChatsType.value}
              defaultValueShow={showChatsType.format}
              change={handleShowChatsTypeChange}
              downloadParams={{
                ...allData.table,
                name: activeCard === 'Profit' ? formatMessage({ id: "dbs_profit_trend" }) : formatMessage({ id: "dbs_gross_revenue_trend" }),
              }}
              options={pickOperations(['view', ...showRevenueTrendList, 'download'], formatMessage)}>
            </RenderOperations>
          }
        >
          {showChatsType?.value === 'bar' ? <Justify
            className="dbs-justify-half"
            bottom
            left={<div className='dbs-revenue-now-date'>
              {Object.keys(currentMetricsData).map((item: any) => <div className='dbs-home-overview-item' key={item}>
                <div className='dbs-home-overview-item-header'>
                  <div className='dbs-home-overview-item-title'>{currentMetricsData?.[item]?.title}</div>
                </div>
                <div className='dbs-revenue-date-desc'>{currentMetricsData?.[item]?.date}</div>
                <DataProcessing content={['$', currentMetricsData?.[item]?.content]} data={currentMetricsData?.[item]?.data} rate={currentMetricsData?.[item]?.rate || ''} />
              </div>)}
            </div>}
          // right={<RenderOperations
          //   defaultValue={showChatsType.value}
          //   defaultValueShow={showChatsType.format}
          //   change={handleShowChatsTypeChange}
          //   downloadParams={{
          //     ...allData.table,
          //     name: activeCard === 'Profit' ? formatMessage({ id: "dbs_profit_trend" }) : formatMessage({ id: "dbs_gross_revenue_trend" }),
          //   }}
          //   options={pickOperations(['view', ...showRevenueTrendList, 'download'], formatMessage)}>
          // </RenderOperations>}
          /> : <></>}
          {trendLoading && (
            <Loading style={{ height: '443px' }} />
          )}
          {!trendLoading && showChatsType?.value !== 'table' && allData[showChatsType?.value] &&
            <div style={{ position: 'relative' }}>
              <Chart
                data={allData[showChatsType?.value]}
                key={`${activeCard}${showChatsType?.value}${chartEventSetting.showEventBubbles}`}
                type={showChatsType?.value}
                // height={height}
                height="443px"
                // height="593px"
                setOtherConfigFunc={(opt: any) => {
                  opt.grid.containLabel = true;

                  // Adjust grid top spacing for event bubbles
                  if (chartEventSetting.showEventBubbles && Object.keys(chartEventSetting.bubbleEvent || {}).length > 0) {
                    opt.grid.top = 140;
                  }

                  // opt.legend.type="plain"
                  opt = xAxisHighLight(opt, {
                    lineStyle: showChatsType?.value === 'bar' ? {
                      color: 'rgba(216, 218, 228, 0.3)',
                      width: 94,
                      type: 'solid',
                    } : {
                      color: '#BBBDCB',
                      width: 1,
                      type: 'solid',
                    }
                  });

                  // Custom tooltip formatter for Actual/Forecast/KPI series with capsule bars
                  buildTrendTooltipFormatter(opt, {
                    actualName: formatMessage({ id: 'dbs_charts_legend_actual' }),
                    forecastName: formatMessage({ id: 'dbs_charts_legend_forecast' }),
                    kpiName: formatMessage({ id: 'dbs_charts_legend_kpi' }),
                    activeCard,
                    mathFormat,
                    dealTypeValue,
                  });

                  return opt;
                }}
                onChartReady={(chartInstance: any) => {
                  chartRef.current = chartInstance;

                  // Wait for chart to fully render before setting ready state
                  // This ensures TopTipsEvent series is available AND convertToPixel returns valid values
                  const checkChartReady = () => {
                    const chartOption = chartInstance?.getOption();
                    const hasTopTipsEvent = chartOption?.series?.some((s: any) => s.name === 'TopTipsEvent');

                    if (hasTopTipsEvent) {
                      const xAxisData = chartOption?.xAxis?.[0]?.data || [];
                      const testXAxis = xAxisData.find((x: string) => x && x.length > 0);

                      if (testXAxis) {
                        try {
                          const testPosition = chartInstance.convertToPixel(
                            { seriesName: 'TopTipsEvent' },
                            [testXAxis, 0]
                          );
                          if (!isNaN(testPosition[0])) {
                            setIsChartReady(true);
                            return;
                          }
                        } catch (e) {
                          // convertToPixel not ready yet
                        }
                      }
                    }
                    setTimeout(checkChartReady, 100);
                  };

                  // Start checking
                  checkChartReady();
                }}
              />
              {/* Event Windows - only show for bar and line charts - OUTSIDE Chart component as sibling */}
              {(['bar', 'line'].includes(showChatsType?.value)) && isChartReady && chartEventSetting.showEventBubbles && chartRef.current && (
                <EventWindows
                  key={Object.keys(chartEventSetting.bubbleEvent || {}).join(',')}
                  myChart={chartRef.current}
                  eventList={chartEventSetting.bubbleEvent}
                  format="YYYY-MM"
                  maxTimeValue={revenueData[revenueData.length - 1]?.month || ''}
                  showEventAxis={false}
                  dateType="monthly"
                  // Pass full xAxisData including empty strings to match chart's xAxis data exactly
                  // EventWindows will handle the empty strings internally
                  xAxisData={allData[showChatsType?.value]?.xAxisData || []}
                  yAxisMin={false}
                  topPadding={20}
                />
              )}
            </div>}
          {!trendLoading && showChatsType?.value === 'table' && allData[showChatsType?.value] && <TableContent
            className="dbs-commons-table dbs-chats-table"
            key={`${activeCard}${showChatsType?.value}`}
            // height={'360px'}
            // ref={tableContent}
            // searchForm={searchForm}
            datas={allData[showChatsType?.value]?.data}
            columns={allData[showChatsType?.value]?.columns}
            sort={[{ by: 'fill_time', order: 'desc' }]}
            resultKey="list"
            recordKey="date"
            // loading={tableLoading}
            // preParams={preParams}
            // lastResultData={lastResultData}
            // scroll={{ x: 'max-content', y: 55 * 5 }}
            // handerSearch={true}
            // immediately={false}
            addons={[
              // 支持表格滚动，高度超过 192 开始显示滚动条
              scrollable({
                // maxHeight: '310px',
                // minWidth: 2000,
              }),
            ]}
          ></TableContent>}

          <PhaseStudioTable
            activeCard={activeCard}
            selectedMonth={selectedMonth}
            canEdit={buttonPermission.grossRevenueTrend}
            onEditingChange={handlePhaseEditingChange}
          />
        </CardCommon>

        {/* Event Setting Modal */}
        <EventSetting
          visible={eventSettingVisible}
          close={() => setEventSettingVisible(false)}
          confirm={(params: any) => {
            setChartEventSetting((prev: any) => ({
              ...prev,
              bubbleEvent: params.bubbleEvent,
            }));
            setEventSettingVisible(false);
          }}
          data={eventDataByGame}
          timeFormat="YYYY-MM"
          dateType="monthly"
          xAxisData={allData[showChatsType?.value]?.xAxisData?.filter((x: string) => x !== '') || []}
        />

        <div ref={notesRef} style={{ position: 'relative', top: '-50px', margin: '20px' }}></div>
        <PlainText
          className='dbs-overview-plaintext'
          // key={`plaintext-${selectedProject.unified_id}`}
          title={formatMessage({ id: 'dbs_notes' })}
          url="/api/v1/mgmt_pc/note/get"
          editUrl="/api/v1/mgmt_pc/note/update"
          params={notesParams}
          showDatePicker={false}
          selectedMonth={selectedMonthMoment}
          maxDataTime={maxDataTime}
          showTimeInTitle={true}
          editable={true}
          userPermissions={{ canEdit: buttonPermission.notes }}
          // tipString={'Data is updated by the Studio Operations Management team.'}
          autoSavePageKey={EDIT_PAGE_KEY_MAP.studio_overview_notes}
          // autoSaveId={selectedProject.unified_id}
          // File Upload
          allowFileUpload={true}
          uploadDomain="mgmt/studio/portfolio_overview/notes"
          cosConfigDomain="mgmt/studio/portfolio_overview/notes"
          onEditingChange={handleNotesEditingChange}
          onEditClick={(type) => {
            // Track buried point for Notes Edit button or Add Content link click
            const extInfo = type === 'Add Content Link'
              ? 'Notes Edit - Add Content Link'
              : 'Notes Edit - Edit Button';
            const btnPoint = createBtnPoint(
              PortfolioOverviewTab,
              'notesEdit',
              extInfo,
            );
            if (btnPoint) {
              SGBuriedBtnPoint(btnPoint);
            }
          }}
        />

        {/* Burn Rate + Headcount / Staff Rate — after Notes, before Board Governance */}
        <CardCommon
          title={
            <Segment
              className="dbs-gradient-segment"
              options={metricsSegmentOptions}
              value={metricsDateTypeSegment}
              onChange={(value) => {
                const buttonKey = value === 'year' ? 'metricsByYear' : 'metricsByMonth';
                const btnPoint = createBtnPoint(
                  PortfolioOverviewTab,
                  buttonKey,
                  value === 'year' ? 'By Year' : 'By Month',
                );
                if (btnPoint) {
                  SGBuriedBtnPoint(btnPoint);
                }
                setMetricsDateTypeSegment(value);
              }}
            />
          }
          operation={
            <div className="dbs-card-slot-item">
              <CommonBubble content={formatMessage({ id: 'dbs_download' })} dark>
                <i
                  className="card-slot-item dbo-show"
                  onClick={() => {
                    const btnPoint = createBtnPoint(
                      PortfolioOverviewTab,
                      'metricsDownload',
                      'Portfolio Metrics - Download',
                    );
                    if (btnPoint) {
                      SGBuriedBtnPoint(btnPoint);
                    }
                    setMetricsDownloadVisible(true);
                  }}
                >
                  <SvgIcon type="download" className="card-slot-charicon" />
                </i>
              </CommonBubble>
            </div>
          }
        >
          <div className="dbs-home-studio-card dbs-portfolio-metrics-card">
            <BurnRateCard
              activeDateTypeSegment={metricsDateTypeSegment}
              selectedMonth={selectedMonth}
              smallChartViewState={smallChartViewState}
              updateStudioEyesViewState={updateStudioEyesViewState}
              buriedPointTab={PortfolioOverviewTab}
            />
            <HeadcountCard
              scope="portfolio"
              activeDateTypeSegment={metricsDateTypeSegment}
              selectedMonth={selectedMonth}
              smallChartViewState={smallChartViewState}
              updateStudioEyesViewState={updateStudioEyesViewState}
              buriedPointTab={PortfolioOverviewTab}
            />
          </div>
        </CardCommon>
        <MetricsDownloadModal
          visible={metricsDownloadVisible}
          config={PORTFOLIO_METRICS_DOWNLOAD_CONFIG}
          activeDateTypeSegment={metricsDateTypeSegment}
          selectedMonth={selectedMonth}
          modalSize={500}
          onClose={() => setMetricsDownloadVisible(false)}
        />




        {/* Pipeline Calendar */}
        <PipelineCalendar editable={buttonPermission.pipelineCalendar} calendarRef={pipelineCalendarRef} />
        {/* <CardCommon title={
          <div style={{ fontSize: '22px', marginBottom: '20px' }}>
            {`${formatMessage({ id: 'dbs_pipeline_calendar' })} ${selectedMonth ? moment(selectedMonth, 'YYYY-MM').year() : moment().year()}`}
          </div>
        }
          style={{ marginTop: '20px' }}
        >
          <TimeLine options={qAllData}>
          </TimeLine>
        </CardCommon> */}
      </div>

    </>
  );
};

export default PortfolioOverview;
